import { Component, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  DsButtonVariants,
  DsModalService,
  DsModalSlideOutAlignment,
  DsModalVariant,
  DsTooltipPosition,
  Icons,
  IconStyles,
} from '@levelaccess/design-system';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { RequiredSecurities } from '../../../../../shared/constants/required-securities';
import { FeatureFlagCollection } from '../../../../../shared/interfaces/feature-flag.interface';
import { AiService } from '../../../services/ai.service';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import { LauncherNavService } from '../../../services/navigation/launcher-nav.service';
import { INavigationContextItem, INavMenuItem, NavigationItem } from '../../../services/navigation/nav-structure';
import { NavService } from '../../../services/navigation/nav.service';

export interface NavLink {
  id?: NavigationItem;
  routerLink?: string;
  externalLink?: string;
  label: string;
  ariaLabel?: string;
  isActive: boolean;
  hasChildren: boolean;
  isHidden?: boolean;
  isHiddenOnCurrentScreenSize?: boolean;
  iconName?: Icons;
}

@Component({
  selector: 'compound-second-level-navbar',
  templateUrl: './compound-second-level-navbar.component.html',
  styleUrls: ['./compound-second-level-navbar.component.scss'],
  standalone: false,
})
export class CompoundSecondLevelNavbarComponent implements OnInit {
  @ViewChild('launcherModal') public launcherModal: TemplateRef<any>;

  @Input() public showMobileNavVersion: boolean;

  public Icons: typeof Icons;
  public IconStyles: typeof IconStyles = IconStyles;
  public DsButtonVariants: typeof DsButtonVariants;
  public DsTooltipPosition: typeof DsTooltipPosition;
  protected readonly RequiredSecurities: typeof RequiredSecurities = RequiredSecurities;
  protected readonly FeatureFlagCollection: typeof FeatureFlagCollection = FeatureFlagCollection;

  public childrenRoutes$: Observable<NavLink[]>;
  protected showChat$: Observable<boolean>;
  protected hasAccessToLauncher: boolean;
  protected currentContextItem$: Observable<INavigationContextItem>;

  constructor(
    private navService: NavService,
    private dsModalService: DsModalService,
    private featureFlagService: FeatureFlagService,
    private launcherNavService: LauncherNavService,
    private aiService: AiService,
  ) {
    this.Icons = Icons;
    this.IconStyles = IconStyles;
    this.DsButtonVariants = DsButtonVariants;
    this.DsTooltipPosition = DsTooltipPosition;
  }

  private handleActiveTopLevelMenuItemChanges(activeMenuItem?: INavMenuItem): NavLink[] {
    const children: INavMenuItem[] = activeMenuItem?.children ?? [];

    return children
      .filter((child: INavMenuItem): boolean => child.isAvailable && !child.isHidden)
      .map((child: INavMenuItem): NavLink => ({
        routerLink: child.routerLink,
        externalLink: child.externalLink,
        label: child.label,
        ariaLabel: child.ariaLabel,
        isActive: child.isActive,
        hasChildren: child.children?.length > 0,
      }));
  }

  public openLauncher(): void {
    this.dsModalService.open(this.launcherModal, DsModalVariant.slideOut, DsModalSlideOutAlignment.start);
  }

  ngOnInit(): void {
    this.childrenRoutes$ = this.navService.activeTopLevelMenuItem$().pipe(map(this.handleActiveTopLevelMenuItemChanges));

    this.hasAccessToLauncher = this.launcherNavService.hasAccessToLauncher;
    this.currentContextItem$ = this.navService.getCurrentContextItem$();

    this.showChat$ = combineLatest([
      this.aiService.aiEnabledForTenant$,
      this.featureFlagService.variation$(FeatureFlagCollection.aiChat, false),
      this.navService.isVerticalNavigationEnabled$,
    ]).pipe(
      map(([aiEnabledForTenant, aiChatFlagEnabled, isVerticalNavigationEnabled]: [boolean, boolean, boolean]): boolean => {
        return aiEnabledForTenant && aiChatFlagEnabled && !isVerticalNavigationEnabled;
      }),
    );
  }
}
